import {
  FILTROS_SOLICITACOES,
  type AcoesSolicitacao,
  type AlteracaoSolicitada,
  type ContagemSolicitacoes,
  type SolicitacaoSecretaria,
  type SolicitacoesSecretariaResposta,
} from '@/features/secretaria-arbitral/types/solicitacoes';
import { resolveBackendUrl } from '@/shared/utils/requestBuilder';

function contagemVazia(): ContagemSolicitacoes {
  return Object.fromEntries(
    FILTROS_SOLICITACOES.map((filtro) => [filtro.value, 0]),
  ) as ContagemSolicitacoes;
}

function contagemNormalizada(bruta: unknown): ContagemSolicitacoes {
  const base = contagemVazia();
  if (typeof bruta !== 'object' || bruta === null) return base;
  const origem = bruta as Record<string, unknown>;
  for (const filtro of FILTROS_SOLICITACOES) {
    const valor = origem[filtro.value];
    if (typeof valor === 'number' && Number.isFinite(valor)) base[filtro.value] = valor;
  }
  return base;
}

function acoesNormalizadas(bruta: unknown): AcoesSolicitacao {
  const origem = (typeof bruta === 'object' && bruta !== null ? bruta : {}) as Record<
    string,
    unknown
  >;
  return {
    aceitar: origem.aceitar === true,
    recusar: origem.recusar === true,
    concluso: origem.concluso === true,
    liberarAdvogado: origem.liberarAdvogado === true,
  };
}

function solicitanteNormalizado(bruto: unknown): SolicitacaoSecretaria['solicitante'] {
  const origem = (typeof bruto === 'object' && bruto !== null ? bruto : {}) as Record<
    string,
    unknown
  >;
  return {
    nome: typeof origem.nome === 'string' ? origem.nome : '—',
    papel:
      typeof origem.papel === 'string'
        ? (origem.papel as SolicitacaoSecretaria['solicitante']['papel'])
        : 'DESCONHECIDO',
    papelLabel: typeof origem.papelLabel === 'string' ? origem.papelLabel : '—',
    oab: typeof origem.oab === 'string' && origem.oab !== '' ? origem.oab : null,
  };
}

function anexoNormalizado(bruto: unknown): SolicitacaoSecretaria['anexo'] {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const origem = bruto as Record<string, unknown>;
  if (typeof origem.id !== 'string' || origem.id === '') return null;
  if (typeof origem.downloadUrl !== 'string' || origem.downloadUrl === '') return null;
  return {
    id: origem.id,
    nome: typeof origem.nome === 'string' && origem.nome !== '' ? origem.nome : 'Anexo',
    tamanhoBytes: typeof origem.tamanhoBytes === 'number' ? origem.tamanhoBytes : 0,
    downloadUrl: resolveBackendUrl(origem.downloadUrl),
  };
}

function alteracoesNormalizadas(bruta: unknown): AlteracaoSolicitada[] {
  if (!Array.isArray(bruta)) return [];
  return bruta.flatMap((alteracao) => {
    if (typeof alteracao !== 'object' || alteracao === null) return [];
    const origem = alteracao as Record<string, unknown>;
    if (typeof origem.campo !== 'string' || origem.campo === '') return [];
    if (typeof origem.para !== 'string' || origem.para === '') return [];
    return [
      {
        campo: origem.campo,
        de: typeof origem.de === 'string' ? origem.de : null,
        para: origem.para,
      },
    ];
  });
}

function itemNormalizado(bruto: unknown): SolicitacaoSecretaria | null {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const origem = bruto as Record<string, unknown>;
  if (typeof origem.id !== 'string' || origem.id === '') return null;
  return {
    ...(origem as unknown as SolicitacaoSecretaria),
    solicitante: solicitanteNormalizado(origem.solicitante),
    alteracoes: alteracoesNormalizadas(origem.alteracoes),
    acoes: acoesNormalizadas(origem.acoes),
    anexo: anexoNormalizado(origem.anexo),
    conclusoEncerradoPelaSentenca: origem.conclusoEncerradoPelaSentenca === true,
  };
}

export function normalizarSolicitacoesResposta(bruta: unknown): SolicitacoesSecretariaResposta {
  const origem = (typeof bruta === 'object' && bruta !== null ? bruta : {}) as Record<
    string,
    unknown
  >;
  return {
    itens: Array.isArray(origem.itens)
      ? origem.itens
          .map(itemNormalizado)
          .filter((item): item is SolicitacaoSecretaria => item !== null)
      : [],
    contagem: contagemNormalizada(origem.contagem),
    arbitragemInstaurada: origem.arbitragemInstaurada === true,
    arbitroNome: typeof origem.arbitroNome === 'string' ? origem.arbitroNome : null,
  };
}
